// src/lib/exportUtils.js
// Phase-3C: Export utilities for PDF, Image, CSV, and WhatsApp sharing
// Client-side only - no backend endpoints required

import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

/**
 * Generate filename with date range
 * Format: Coil_{coil_no}_{fromDate}_{toDate}.{ext}
 */
export const generateFilename = (prefix, coilNo, ext, fromDate = null, toDate = null) => {
  const sanitizedCoilNo = (coilNo || 'unknown').toString().replace(/[^a-zA-Z0-9]/g, '_');
  const today = new Date().toISOString().slice(0, 10);
  const from = fromDate || today;
  const to = toDate || today;
  return `${prefix}_${sanitizedCoilNo}_${from}_${to}.${ext}`;
};

/**
 * Check if Web Share API is available (typically mobile)
 */
export const canUseWebShare = () => {
  return navigator.share && navigator.canShare;
};

/**
 * Export element to PNG image
 * @param {HTMLElement} element - DOM element to capture
 * @param {string} filename - Output filename
 * @returns {Promise<Blob>} - Image blob
 */
export const exportToImage = async (element, filename = 'export.png') => {
  if (!element) throw new Error('Element not found for export');
  
  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    logging: false,
  });
  
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve({ blob, filename, canvas });
    }, 'image/png', 0.95);
  });
};

/**
 * Export element to PDF
 * @param {HTMLElement} element - DOM element to capture
 * @param {string} filename - Output filename
 * @param {string} title - PDF title/header
 * @returns {Promise<Blob>} - PDF blob
 */
export const exportToPDF = async (element, filename = 'export.pdf', title = '') => {
  if (!element) throw new Error('Element not found for export');
  
  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    logging: false,
  });
  
  const imgData = canvas.toDataURL('image/png');
  const imgWidth = canvas.width;
  const imgHeight = canvas.height;
  
  // Calculate PDF dimensions (A4 ratio)
  const pdfWidth = 210; // A4 width in mm
  const pdfHeight = (imgHeight * pdfWidth) / imgWidth;
  
  const pdf = new jsPDF({
    orientation: pdfHeight > pdfWidth ? 'portrait' : 'landscape',
    unit: 'mm',
    format: [pdfWidth, Math.max(pdfHeight, 297)],
  });
  
  // Add title if provided
  if (title) {
    pdf.setFontSize(14);
    pdf.text(title, 10, 10);
    pdf.addImage(imgData, 'PNG', 0, 15, pdfWidth, pdfHeight);
  } else {
    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
  }
  
  const blob = pdf.output('blob');
  return { blob, filename, pdf };
};

/**
 * Export data to CSV
 * @param {Array<Object>} data - Array of objects to export
 * @param {Array<{key: string, label: string}>} columns - Column definitions
 * @param {string} filename - Output filename
 * @returns {Blob} - CSV blob
 */
export const exportToCSV = (data, columns, filename = 'export.csv') => {
  if (!data || data.length === 0) throw new Error('No data to export');
  
  // Build header row
  const headers = columns.map(col => `"${col.label}"`).join(',');
  
  // Build data rows
  const rows = data.map(row => {
    return columns.map(col => {
      let value = row[col.key];
      // Handle null/undefined
      if (value === null || value === undefined) value = '';
      // Handle dates
      if (value instanceof Date) value = value.toISOString();
      // Handle objects
      if (typeof value === 'object') value = JSON.stringify(value);
      // Escape quotes and wrap in quotes
      return `"${String(value).replace(/"/g, '""')}"`;
    }).join(',');
  });
  
  const csvContent = [headers, ...rows].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  
  return { blob, filename };
};

/**
 * Download blob as file
 * @param {Blob} blob - File blob
 * @param {string} filename - Output filename
 */
export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Share via WhatsApp
 * - Mobile: Uses Web Share API to share file directly
 * - Desktop: Downloads file and opens WhatsApp Web
 * 
 * @param {Blob} blob - File blob to share
 * @param {string} filename - Filename
 * @param {string} message - Optional message text
 * @returns {Promise<{method: 'webshare'|'desktop', success: boolean}>}
 */
export const shareViaWhatsApp = async (blob, filename, message = '') => {
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  // Try Web Share API (mobile)
  if (canUseWebShare() && isMobile) {
    try {
      const file = new File([blob], filename, { type: blob.type });
      const shareData = {
        files: [file],
        title: filename,
        text: message,
      };
      
      if (navigator.canShare(shareData)) {
        await navigator.share(shareData);
        return { method: 'webshare', success: true };
      }
    } catch (err) {
      // User cancelled or share failed - fall through to desktop method
      if (err.name !== 'AbortError') {
        console.warn('Web Share failed:', err);
      }
    }
  }
  
  // Desktop fallback: Download + open WhatsApp Web
  downloadBlob(blob, filename);
  
  // Encode message for WhatsApp Web URL
  const encodedMessage = encodeURIComponent(message || `Sharing: ${filename}`);
  const whatsappUrl = `https://web.whatsapp.com/send?text=${encodedMessage}`;
  
  // Open WhatsApp Web in new tab
  window.open(whatsappUrl, '_blank');
  
  return { method: 'desktop', success: true };
};

/**
 * Combined export and share function
 * @param {'pdf'|'image'|'csv'} type - Export type
 * @param {HTMLElement|null} element - DOM element (for pdf/image)
 * @param {Object} options - Export options
 */
export const exportAndShare = async (type, element, options = {}) => {
  const {
    filename,
    title,
    data,
    columns,
    shareVia,
    message,
  } = options;
  
  let result;
  
  switch (type) {
    case 'pdf':
      result = await exportToPDF(element, filename, title);
      break;
    case 'image':
      result = await exportToImage(element, filename);
      break;
    case 'csv':
      result = exportToCSV(data, columns, filename);
      break;
    default:
      throw new Error(`Unknown export type: ${type}`);
  }
  
  if (shareVia === 'whatsapp') {
    return shareViaWhatsApp(result.blob, result.filename, message);
  } else {
    downloadBlob(result.blob, result.filename);
    return { method: 'download', success: true };
  }
};

export default {
  generateFilename,
  canUseWebShare,
  exportToImage,
  exportToPDF,
  exportToCSV,
  downloadBlob,
  shareViaWhatsApp,
  exportAndShare,
};
